import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { getCapabilities } from "../../utils/capabilities";

export default function PriceHistoryDialog({
  open,
  onClose,
  variantId,
  itemLabel,
  user,
}) {
  const { t, i18n } = useTranslation();
  const { country } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const { caps } = useMemo(() => getCapabilities({ user }), [user]);
  const canViewHistory = caps.priceHistory.available;

  const maxDays = useMemo(() => (canViewHistory ? caps.priceHistory.maxDays : 0), [canViewHistory, caps.priceHistory.maxDays]);

  useEffect(() => {
    if (!open || !variantId || !canViewHistory) return;
    let mounted = true;
    setLoading(true);
    setError("");
    api.price
      .getVariantHistory(variantId, maxDays)
      .then((res) => {
        if (!mounted) return;
        setPayload(res?.data || null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load price history."
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, variantId, maxDays, canViewHistory]);

  const history = payload?.history || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("price_history") || "Price history"}
        {itemLabel ? ` - ${itemLabel}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>{t("loading") || "Loading..."}</Typography>
          </Box>
        ) : !canViewHistory ? (
          <Alert severity="info">
            {user
              ? (t("premium_required_desc") || "This feature is available only for Premium users.")
              : (t("login_required_title") || "Please log in to continue.")}
          </Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              {(t("history_window_label") || "Visible history window")}: {payload?.applied_days || maxDays}{" "}
              {(i18n.language === "el" || i18n.language === "gr") ? "ημέρες" : "days"}
            </Typography>
            {history.length === 0 ? (
              <Alert severity="info">
                {t("no_price_history") || "No price history available yet."}
              </Alert>
            ) : (
              <List dense>
                {history.map((row) => (
                  <ListItem key={row.id} divider>
                    <ListItemText
                      primary={`${Number(row.price || 0).toFixed(2)} ${payload?.variant?.price_per_kg || payload?.variant?.price_per_liter ? "" : ""}`}
                      secondary={`${new Date(row.captured_at).toLocaleDateString(i18n.language || "en")} · ${
                        row.price_per_kg != null
                          ? `${Number(row.price_per_kg).toFixed(2)} / kg`
                          : row.price_per_liter != null
                            ? `${Number(row.price_per_liter).toFixed(2)} / L`
                            : t("unit_price_not_available") || "Unit price unavailable"
                      }`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!canViewHistory ? (
          <Button onClick={() => navigate(`/${country || "el"}/${user ? "plans?intent=premium" : "login"}`)}>
            {user ? (t("pricing.upgrade") || "Upgrade") : (t("pricing.login") || "Login")}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t("close") || "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
